import i18n from "i18next";
import { FieldPath } from "react-hook-form";
import { Country } from "src/api/generated/io/aavo/applications/db/erp/types/country.ts";
import { FormSelectField, FormSelectFieldProps } from "src/components/common/forms/fields/FormSelectField.tsx";
import {
	COUNTRY_OPTIONS,
	COUNTRY_OPTIONS_BY_CODE,
	CountryOption,
	DEFAULT_COUNTRY_CODE,
	getCountryFlag,
	getCountryLabel,
	getCountryOptionLabel,
} from "src/components/common/country/countries.ts";
import { useStoredState } from "src/utils/useStoredState.ts";

export interface FormCountrySelectFieldProps<
	TFieldValues extends object,
	TFieldName extends FieldPath<TFieldValues>,
	DisableClearable extends boolean | undefined,
> extends Omit<
	FormSelectFieldProps<TFieldValues, TFieldName, Country, CountryOption, DisableClearable>,
	"options" | "getOptionKey" | "getOptionLabel"
> {}

export const FormCountrySelectField = <
	TFieldValues extends object,
	TFieldName extends FieldPath<TFieldValues>,
	DisableClearable extends boolean | undefined = true,
>({
	disableClearable = true,
	onChange: onChangeProp,
	...other
}: FormCountrySelectFieldProps<TFieldValues, TFieldName, DisableClearable>) => {
	const [pinnedCountries, setPinnedCountries] = useStoredState<Country[]>({
		type: "object",
		key: "A7C3E9F1D2B84560",
		defaultValue: DEFAULT_PINNED_COUNTRY_CODES,
	});

	return (
		<FormSelectField<TFieldValues, TFieldName, Country, CountryOption, DisableClearable>
			options={getCountryOptions()}
			getOptionKey={(option) => option.code}
			getOptionLabel={(option) => `${getCountryFlag(option.code)} ${getCountryLabel(option.code)}`}
			renderOption={(props, option) => {
				return (
					<li {...props}>
						{getCountryFlag(option.code)} {getCountryLabel(option.code)}
					</li>
				);
			}}
			groupBy={(option) =>
				isPinnedCountry(option.code) ?
					i18n.t("common_countries")
				:	i18n.t("all_countries")
			}
			onChange={(key, option) => {
				if (key != null) {
					setPinnedCountries(computeUpdatedPinnedCountries(key));
				}
				onChangeProp?.(key, option);
			}}
			disableClearable={disableClearable as DisableClearable}
			{...other}
		/>
	);

	function getCountryOptions(): CountryOption[] {
		const pinned = pinnedCountries
			.map((countryCode) => COUNTRY_OPTIONS_BY_CODE[countryCode])
			.filter((option): option is CountryOption => option != null);

		const rest = COUNTRY_OPTIONS.filter((option) => !isPinnedCountry(option.code)).sort((a, b) =>
			getCountryOptionLabel(a.labelKey).localeCompare(getCountryOptionLabel(b.labelKey)),
		);

		return [...pinned, ...rest];
	}

	function computeUpdatedPinnedCountries(selectedCountry: Country): Country[] {
		if (selectedCountry === DEFAULT_COUNTRY_CODE) return pinnedCountries;
		if (isPinnedCountry(selectedCountry)) return pinnedCountries;

		const withoutLast = pinnedCountries.slice(0, -1);
		const defaultIndex = withoutLast.indexOf(DEFAULT_COUNTRY_CODE);

		// Insert after the default country, which always stays first
		const insertIndex = defaultIndex >= 0 ? defaultIndex + 1 : 0;
		return [...withoutLast.slice(0, insertIndex), selectedCountry, ...withoutLast.slice(insertIndex)].slice(
			0,
			PINNED_COUNTRIES_COUNT,
		);
	}

	function isPinnedCountry(countryCode: Country): boolean {
		return pinnedCountries.some((code) => code === countryCode);
	}
};

const DEFAULT_PINNED_COUNTRY_CODES: Country[] = [
	"FINLAND",
	"SWEDEN",
	"NORWAY",
	"ESTONIA",
	"GERMANY",
	"DENMARK",
	"UNITED_STATES",
];

const PINNED_COUNTRIES_COUNT = DEFAULT_PINNED_COUNTRY_CODES.length;
